import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  options: SelectOption[];
  placeholder?: string;
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
}

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  font-family: ${({ theme }) => theme.fonts.primary};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const SelectButton = styled.button<{ hasError: boolean; isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme, hasError, isOpen }) => 
    hasError 
      ? theme.colors.error 
      : isOpen 
        ? theme.colors.secondary 
        : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  text-align: left;
  transition: ${({ theme }) => theme.transitions.fast};
  box-shadow: ${({ isOpen, theme }) => isOpen ? theme.boxShadow.sm : 'none'};
  min-height: 44px; /* Minimum touch target size */
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[3]};
    font-size: ${({ theme }) => theme.fontSizes.md};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.backgroundAlt};
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme, hasError }) => 
      hasError ? theme.colors.error : theme.colors.secondary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, hasError }) => 
      hasError ? theme.colors.error : theme.colors.secondary};
  }
`;

const DropdownIcon = styled.div<{ isOpen: boolean }>`
  width: 0.5rem;
  height: 0.5rem;
  border-right: 2px solid ${({ theme }) => theme.colors.text};
  border-bottom: 2px solid ${({ theme }) => theme.colors.text};
  transform: ${({ isOpen }) => 
    isOpen ? 'rotate(-135deg)' : 'rotate(45deg)'};
  transition: ${({ theme }) => theme.transitions.fast};
  margin-left: ${({ theme }) => theme.spacing[2]};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 0.625rem;
    height: 0.625rem;
  }
`;

const OptionsContainer = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  max-height: 50vh;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  z-index: 20;
  box-shadow: ${({ theme }) => theme.boxShadow.md};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    max-height: 15rem;
  }
`;

const Option = styled.div<{ isSelected: boolean }>`
  padding: ${({ theme }) => theme.spacing[3]};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  background-color: ${({ theme, isSelected }) => 
    isSelected ? theme.colors.backgroundAlt : 'transparent'};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  min-height: 44px; /* Minimum touch target size */
  display: flex;
  align-items: center;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundAlt};
  }
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

export const Select: React.FC<SelectProps> = ({
  options,
  placeholder = 'Select an option',
  label,
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  
  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelectOption = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedOption = options.find(option => option.value === value);

  return (
    <SelectContainer ref={selectRef}>
      {label && <Label>{label}</Label>}
      
      <SelectButton 
        type="button"
        onClick={handleToggleDropdown}
        disabled={disabled}
        hasError={!!error}
        isOpen={isOpen}
      >
        {selectedOption ? selectedOption.label : placeholder}
        <DropdownIcon isOpen={isOpen} />
      </SelectButton>
      
      <OptionsContainer isOpen={isOpen}>
        {options.map(option => (
          <Option
            key={option.value}
            isSelected={option.value === value}
            onClick={() => handleSelectOption(option.value)}
          >
            {option.label}
          </Option>
        ))}
      </OptionsContainer>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </SelectContainer>
  );
};

export default Select; 